/**
 * Project API Service
 *
 * Provides standardized methods for interacting with the Project API endpoints
 */
import api from './api';
import { useAuthStore } from '../stores/auth';

/**
 * Project service with standardized methods for project-related API operations
 */
export const projectService = {
  /**
   * Get all projects with optional filtering and pagination
   * @param {Object} params - Query parameters (page, limit, filter, etc.)
   * @returns {Promise<Object>} Paginated projects with metadata
   */
  async getProjects(params = {}) {
    return api.getPaginated('projects', params);
  },

  /**
   * Get a single project by ID with optional includes
   * @param {number|string} id - Project ID
   * @param {Array|string} include - Related data to include
   * @returns {Promise<Object>} Project data
   */
  async getProject(id, include = []) {
    const params = {};
    if (include) {
      params.include = include;
    }
    return api.get(`projects/${id}`, params);
  },

  /**
   * Create a new project
   * @param {Object} projectData - Project data
   * @returns {Promise<Object>} Created project
   */
  async createProject(projectData) {
    return api.post('projects', projectData);
  },

  /**
   * Update an existing project
   * @param {number|string} id - Project ID
   * @param {Object} projectData - Updated project data
   * @returns {Promise<Object>} Updated project
   */
  async updateProject(id, projectData) {
    return api.put(`projects/${id}`, projectData);
  },

  /**
   * Archive a project
   * @param {number|string} id - Project ID
   * @returns {Promise<Object>} Archived project
   */
  async archiveProject(id) {
    return api.delete(`projects/${id}`);
  },

  /**
   * Restore an archived project
   * @param {number|string} id - Project ID
   * @returns {Promise<Object>} Restored project
   */
  async restoreProject(id) {
    return api.post(`projects/${id}/restore`);
  },

  /**
   * Convert a tender to project
   * @param {number|string} tenderId - Tender ID
   * @param {Object} conversionData - Conversion settings
   * @returns {Promise<Object>} Created project
   */
  async convertTenderToProject(tenderId, conversionData) {
    return api.post(`tenders/${tenderId}/convert-to-project`, conversionData);
  },

  // ==================== TASKS ====================

  /**
   * Get all tasks for a project
   * @param {number|string} projectId - Project ID
   * @param {Object} params - Query parameters
   * @returns {Promise<Object>} Tasks data
   */
  async getTasks(projectId, params = {}) {
    return api.get(`projects/${projectId}/tasks`, params);
  },

  /**
   * Get a single task
   * @param {number|string} projectId - Project ID
   * @param {number|string} taskId - Task ID
   * @returns {Promise<Object>} Task data
   */
  async getTask(projectId, taskId) {
    return api.get(`projects/${projectId}/tasks/${taskId}`);
  },

  /**
   * Create a new task
   * @param {number|string} projectId - Project ID
   * @param {Object} taskData - Task data
   * @returns {Promise<Object>} Created task
   */
  async createTask(projectId, taskData) {
    return api.post(`projects/${projectId}/tasks`, taskData);
  },

  /**
   * Update a task
   * @param {number|string} projectId - Project ID
   * @param {number|string} taskId - Task ID
   * @param {Object} taskData - Updated task data
   * @returns {Promise<Object>} Updated task
   */
  async updateTask(projectId, taskId, taskData) {
    return api.put(`projects/${projectId}/tasks/${taskId}`, taskData);
  },

  /**
   * Delete a task
   * @param {number|string} projectId - Project ID
   * @param {number|string} taskId - Task ID
   * @returns {Promise<Object>} Deletion result
   */
  async deleteTask(projectId, taskId) {
    return api.delete(`projects/${projectId}/tasks/${taskId}`);
  },

  /**
   * Mark a task as complete
   * @param {number|string} projectId - Project ID
   * @param {number|string} taskId - Task ID
   * @returns {Promise<Object>} Updated task
   */
  async completeTask(projectId, taskId) {
    return api.post(`projects/${projectId}/tasks/${taskId}/complete`);
  },

  // ==================== TEAM MEMBERS ====================

  /**
   * Get all team members for a project
   * @param {number|string} projectId - Project ID
   * @returns {Promise<Object>} Team members data
   */
  async getTeamMembers(projectId) {
    return api.get(`projects/${projectId}/team`);
  },

  /**
   * Add a team member to a project
   * @param {number|string} projectId - Project ID
   * @param {Object} memberData - Team member data
   * @returns {Promise<Object>} Added team member
   */
  async addTeamMember(projectId, memberData) {
    return api.post(`projects/${projectId}/team`, memberData);
  },

  /**
   * Update a team member
   * @param {number|string} projectId - Project ID
   * @param {number|string} memberId - Team member ID
   * @param {Object} memberData - Updated member data
   * @returns {Promise<Object>} Updated team member
   */
  async updateTeamMember(projectId, memberId, memberData) {
    return api.put(`projects/${projectId}/team/${memberId}`, memberData);
  },

  /**
   * Remove a team member from a project
   * @param {number|string} projectId - Project ID
   * @param {number|string} memberId - Team member ID
   * @returns {Promise<Object>} Removal result
   */
  async removeTeamMember(projectId, memberId) {
    return api.delete(`projects/${projectId}/team/${memberId}`);
  },

  // ==================== DOCUMENTS ====================

  /**
   * Get all documents for a project
   * @param {number|string} projectId - Project ID
   * @returns {Promise<Object>} Documents data
   */
  async getDocuments(projectId) {
    return api.get(`projects/${projectId}/documents`);
  },

  /**
   * Upload a document to a project
   * @param {number|string} projectId - Project ID
   * @param {FormData} formData - Form data with file and metadata
   * @returns {Promise<Object>} Upload result
   */
  async uploadDocument(projectId, formData) {
    // Create custom headers without Content-Type to let browser set it with boundary
    const headers = api.getHeaders(true);

    // Custom request for file upload
    try {
      const url = `${'/api'}/projects/${projectId}/documents`;
      const response = await fetch(url, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: formData,
      });

      return api.handleResponse(response);
    } catch (error) {
      throw api.handleError(error);
    }
  },

  /**
   * Update document metadata
   * @param {number|string} projectId - Project ID
   * @param {number|string} docId - Document ID
   * @param {Object} docData - Updated document data
   * @returns {Promise<Object>} Updated document
   */
  async updateDocument(projectId, docId, docData) {
    return api.put(`projects/${projectId}/documents/${docId}`, docData);
  },

  /**
   * Delete a document
   * @param {number|string} projectId - Project ID
   * @param {number|string} docId - Document ID
   * @returns {Promise<Object>} Deletion result
   */
  async deleteDocument(projectId, docId) {
    return api.delete(`projects/${projectId}/documents/${docId}`);
  },

  /**
   * Get document download URL
   * @param {number|string} projectId - Project ID
   * @param {number|string} docId - Document ID
   * @returns {string} Document download URL with authentication token
   */
  getDocumentDownloadUrl(projectId, docId) {
    // For file downloads we need to use token as query param
    const authStore = useAuthStore();
    const token = authStore.token;
    return `/api/projects/${projectId}/documents/${docId}/download?token=${token}`;
  },

  // ==================== MILESTONES ====================

  /**
   * Get all milestones for a project
   * @param {number|string} projectId - Project ID
   * @returns {Promise<Object>} Milestones data
   */
  async getMilestones(projectId) {
    return api.get(`projects/${projectId}/milestones`);
  },

  /**
   * Create a new milestone
   * @param {number|string} projectId - Project ID
   * @param {Object} milestoneData - Milestone data
   * @returns {Promise<Object>} Created milestone
   */
  async createMilestone(projectId, milestoneData) {
    return api.post(`projects/${projectId}/milestones`, milestoneData);
  },

  /**
   * Update a milestone
   * @param {number|string} projectId - Project ID
   * @param {number|string} milestoneId - Milestone ID
   * @param {Object} milestoneData - Updated milestone data
   * @returns {Promise<Object>} Updated milestone
   */
  async updateMilestone(projectId, milestoneId, milestoneData) {
    return api.put(`projects/${projectId}/milestones/${milestoneId}`, milestoneData);
  },

  /**
   * Delete a milestone
   * @param {number|string} projectId - Project ID
   * @param {number|string} milestoneId - Milestone ID
   * @returns {Promise<Object>} Deletion result
   */
  async deleteMilestone(projectId, milestoneId) {
    return api.delete(`projects/${projectId}/milestones/${milestoneId}`);
  },

  /**
   * Mark a milestone as complete
   * @param {number|string} projectId - Project ID
   * @param {number|string} milestoneId - Milestone ID
   * @returns {Promise<Object>} Updated milestone
   */
  async completeMilestone(projectId, milestoneId) {
    return api.post(`projects/${projectId}/milestones/${milestoneId}/complete`);
  },
};

export default projectService;
